import {useMandantenHandling} from "../../hooks/mandanten/useMandantenHandling";
import {Box, capitalize} from "@mui/material";
import {mandantenStyles} from "../../styles/mandanten/mandanten.styles";
import {Field} from "formik";
import {TextField} from 'formik-mui';
import GenericSelect from "../data/GenericSelect";
import {useUserMandant} from "../../hooks/useUserMandant";
import {Mandant, SpeechModel} from "../../api";
import {useSpeechModelHandling} from "../../hooks/speech_model/useSpeechModelHandling";
import {useFormikContext} from "formik";
import type {MandantFormValues} from "../../types/mandantenTypes";
import {useListPhoneNumbers} from "../../client/queries";
import {useOnboardingHandling} from "../../hooks/onboarding/useOnboardingHandling";
import {useMemo} from "react";

interface FieldsContentMandantProps {
    canEditFields: boolean;
    mandant?: Mandant;
}

export default function FieldsContentMandant({canEditFields, mandant}: FieldsContentMandantProps) {
    const {translation} = useMandantenHandling();
    const {isSystemUser} = useUserMandant();
    const {setFieldValue, values} = useFormikContext<MandantFormValues>();
    const {speechModelOptions, selectedSpeechModel} = useSpeechModelHandling(values.speechModel);
    const {removeNumberPrefix, getCountryCode} = useOnboardingHandling();
    const phoneNumberListe = useListPhoneNumbers({enabled: isSystemUser});

    const phoneNumberOptions = useMemo(
        () => (phoneNumberListe?.items ?? []).map((pn) => ({
            id: removeNumberPrefix(pn.number),
            name: pn.number,
            descriptor: pn.descriptor ?? '',
        })),
        [phoneNumberListe?.items, removeNumberPrefix]
    );

    const selectedPhoneNumber = useMemo(() => {
        const value = values.telefonnummer;
        if (!value) return undefined;
        return phoneNumberOptions.find((option) => option.id === value)
            ?? {id: value, name: `${getCountryCode()}${value}`, descriptor: ''};
    }, [values.telefonnummer, phoneNumberOptions, getCountryCode]);

    return (
        <Box id={'fields-content-mandant-box'}
             display={"grid"}
             sx={mandantenStyles.fieldsContentMandant.box}
        >
            <Box
                id="fields-content-mandant-inline-box"
                display="grid"
                sx={mandantenStyles.fieldsContentMandant.box.inlineBox}
            >
                <Field
                    component={TextField}
                    label={capitalize(translation('client-attributes-id'))}
                    name="id"
                    required
                    disabled={!isSystemUser || mandant}
                />
                <Field
                    component={TextField}
                    label={capitalize(translation('client-attributes-name'))}
                    name="name"
                    required
                    disabled={!canEditFields}
                />
                <Field
                    component={TextField}
                    label={capitalize(translation('client-attributes-external-api-url'))}
                    name="externalApiUrl"
                    disabled={!canEditFields}
                />
            </Box>

            <Field
                sx={mandantenStyles.fieldsContentMandant.descriptionField}
                component={TextField}
                label={capitalize(translation('client-attributes-description'))}
                name='beschreibung'
                disabled={!canEditFields}
                multiline
                rows={5}
            />
            {isSystemUser && (
                <>
                    <Field
                        component={GenericSelect}
                        name={"telefonnummer"}
                        label={capitalize(translation('client-attributes-phone-number'))}
                        options={phoneNumberOptions}
                        getOptionLabel={(option: { id: string, name: string, descriptor?: string }) =>
                            option.descriptor ? `${option.name} — ${option.descriptor}` : option.name}
                        setSelected={(value: string) => setFieldValue("telefonnummer", value)}
                        initialSelected={selectedPhoneNumber}
                        required={false}
                        disabled={!canEditFields}
                    />
                    <Field
                        key={selectedSpeechModel?.id ?? 'default'}
                        component={GenericSelect}
                        name={"speechModel"}
                        label={capitalize(translation('client-attributes-speech-model'))}
                        options={speechModelOptions}
                        getOptionLabel={(option: SpeechModel) => option.name}
                        getSecondaryLabel={(option: SpeechModel) => option.publisher}
                        setSelected={(value: string) => setFieldValue("speechModel", value)}
                        required={false}
                        disabled={!canEditFields}
                        initialSelected={selectedSpeechModel}
                    />
                </>
            )}
        </Box>
    );
}